import React from 'react';
import type { ArtistMediaItem } from 'ts-core';
import { isYouTubeUrl, isInstagramUrl } from '../MediaForm/imageUtils';

function isVideoUrl(url: string): boolean {
    return /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url);
}

/** First displayable photo (the "main image") from a profile media list. */
function firstPhoto(media?: ArtistMediaItem[] | null): string | null {
    if (!media || media.length === 0) return null;
    const photo = media.find(
        (m) =>
            m?.url &&
            m.type !== 'youtube' &&
            m.type !== 'video' &&
            m.type !== 'instagram' &&
            !isYouTubeUrl(m.url) &&
            !isInstagramUrl(m.url) &&
            !isVideoUrl(m.url)
    );
    return photo?.url ?? null;
}

// Deterministic, pleasant fallback colors for the initials avatar.
const FALLBACK_COLORS = ['#DB4C20', '#7C3AED', '#2563EB', '#059669', '#D946EF', '#0891B2', '#EA580C'];

function colorFor(seed: string): string {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    }
    return FALLBACK_COLORS[hash % FALLBACK_COLORS.length];
}

export interface UserAvatarProps {
    name?: string | null;
    /** Explicit account avatar URL (used only if no profile main image is available). */
    avatar?: string | null;
    /** Profile media; the first photo (the "main image") is preferred as the avatar. */
    media?: ArtistMediaItem[] | null;
    /** Precomputed main image URL — overrides `media` resolution when provided. */
    mainImage?: string | null;
    /** Diameter in px. */
    size?: number;
    className?: string;
}

/**
 * Single source of truth for rendering a user/profile avatar. Prefers the
 * profile's main image (first photo), then the explicit account avatar, then a
 * coloured initials circle. Use everywhere a user avatar is shown so the chosen
 * main image is reflected consistently.
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
    name,
    avatar,
    media,
    mainImage,
    size = 40,
    className = '',
}) => {
    const src = mainImage ?? firstPhoto(media) ?? avatar ?? null;
    const dimensions: React.CSSProperties = { width: size, height: size, minWidth: size };

    if (src) {
        return (
            <img
                src={src}
                alt={name || 'avatar'}
                style={dimensions}
                className={`rounded-full object-cover ${className}`}
            />
        );
    }

    const initial = (name?.trim()?.charAt(0) || '?').toUpperCase();
    return (
        <div
            style={{ ...dimensions, backgroundColor: colorFor(name || '?') }}
            className={`rounded-full flex items-center justify-center text-white font-semibold select-none ${className}`}
            aria-label={name || 'avatar'}
        >
            <span style={{ fontSize: Math.round(size * 0.45) }}>{initial}</span>
        </div>
    );
};

export default UserAvatar;
